import { useLayoutEffect, type ReactElement, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useAuthContext } from '@citibox-panel/auth';
import { can, type Permission } from '@/lib/access';
import { registerActiveView, useAccessRecheck } from '@/lib/accessRecheck';
import { useSession } from '@/lib/session';
import { signOutOfPanel } from '@/lib/signOut';
import { reachableHomeHref, type ViewHome } from '@/lib/spaceNavigation';
import { AccessStateScreen } from './AccessStateScreen';

export type { ViewHome } from '@/lib/spaceNavigation';

export interface RequireViewProps {
  permission: Permission;
  // null when the person has nowhere to go: the denial offers only «Cerrar sesión».
  home: ViewHome | null;
  children: ReactNode;
}

// UI gating only. The server becomes the gate once enforcement stage A/B is active in an
// environment; until then a token without the permission can still call these routes. This guard
// decides what the person sees, including after the server refused the view mid-session.
export function RequireView({ permission, home, children }: RequireViewProps): ReactElement {
  const { permissions } = useSession();
  const { firebaseUser } = useAuthContext();
  const { pathname } = useLocation();
  const { refusedViews, recheck } = useAccessRecheck();
  // A layout effect, not a passive one: every layout effect runs before any passive effect, so the
  // view is registered before a child's first read starts and that read is bound to it.
  useLayoutEffect(() => registerActiveView(permission), [permission]);
  const refused = refusedViews.has(permission);
  // Never the refused view's stale content while /me is re-read.
  if (refused && recheck === 'checking') return <AccessStateScreen variant="checking" />;
  const granted = can(permissions, permission);
  if (granted && !refused) return <>{children}</>;
  // Offered only when home would let them in; otherwise a home-less person bounces between denials.
  const homeHref =
    home !== null && refusedViews.has(home.permission)
      ? null
      : reachableHomeHref(home, permissions, pathname);
  return (
    <AccessStateScreen
      variant="denied"
      email={firebaseUser?.email ?? null}
      onSignOut={signOutOfPanel}
      {...(homeHref !== null ? { homeHref } : {})}
      // Still granted by /me but refused by the server: a mismatch only product can fix.
      {...(granted ? { unverified: true } : {})}
    />
  );
}
